/**
 * Which pool a cycle belongs to, and what to call it.
 *
 * BASE v2.0 routes to PRIME and PULSE. BASE v1.3 routed to CORE, FAST and
 * MAJOR. Both sets appear on the same screens at the same time, because the
 * migration is rolling: cycles opened under v1.3 keep running while new ones
 * open under v2.0.
 *
 * A historical cycle is never relabelled. A CORE cycle from 22 August is a
 * CORE cycle, and renaming it PRIME would make the record say the account did
 * something it did not do — the one thing a record must never do. MAJOR is not
 * carried into v2 as a third pool for the same reason in reverse: it does not
 * exist there, so nothing may be shown under that name as if it did.
 *
 * The server names the pool. This module only decides how the name is read,
 * which generation it belongs to, and whether it is still one the system opens
 * cycles into.
 */

/** The pools BASE v2.0 opens cycles into. */
export const CURRENT_POOLS = ["PRIME", "PULSE", "FLASH"] as const;

/** The pools BASE v1.3 opened cycles into, kept for the cycles that used them. */
export const LEGACY_POOLS = ["CORE", "FAST", "MAJOR"] as const;

export type CurrentPool = (typeof CURRENT_POOLS)[number];
export type LegacyPool = (typeof LEGACY_POOLS)[number];

/** Which strategy generation a pool name belongs to. */
export type PoolGeneration = "current" | "legacy" | "unknown";

export function generationOf(pool: string): PoolGeneration {
  if ((CURRENT_POOLS as readonly string[]).includes(pool)) return "current";
  if ((LEGACY_POOLS as readonly string[]).includes(pool)) return "legacy";
  return "unknown";
}

/**
 * What a pool is for, in one line.
 *
 * Only for pools this build can honestly describe. An unknown name is shown as
 * the server sent it with nothing added: inventing a description for a pool
 * nobody here knows would be the browser explaining a decision it did not see.
 */
const PURPOSE: Record<string, string> = {
  PRIME: "устойчивые инструменты; цель выхода снижается с возрастом цикла",
  PULSE: "быстрый оборот: вход по рейтингу, выход от +3%",
  CORE: "BASE v1.3 · основной пул",
  FAST: "BASE v1.3 · быстрый оборот",
  MAJOR: "BASE v1.3 · доля счёта под BTCUSDT",
};

export function purposeOf(pool: string): string | null {
  return PURPOSE[pool] ?? null;
}

/**
 * Whether a pool name still takes new cycles.
 *
 * A legacy pool on a running cycle is normal during the migration and must not
 * be marked as a fault; a legacy pool offered as a destination for something
 * new would be.
 */
export function opensNewCycles(pool: string): boolean {
  return generationOf(pool) === "current";
}
